import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { PlateCalculator } from '../components/PlateCalc';
import { FORMULAS, estimate1RM, percentTable, warmupSets } from '../lib/calc';

const num = (v) => (v === '' ? NaN : parseFloat(v));
const fmt = (n) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

function OneRmTool() {
  const [weight, setWeight] = useState('');
  const [reps, setReps] = useState('');
  const [formula, setFormula] = useState('average');
  const oneRm = estimate1RM(num(weight), num(reps), formula);
  const table = useMemo(() => percentTable(oneRm), [oneRm]);

  return (
    <div className="card">
      <div className="card-title">🧮 Calculateur 1RM &amp; pourcentages</div>
      <div className="form-row">
        <div className="form-group"><label className="form-label">Poids (kg)</label>
          <input type="number" min="0" step="0.5" className="form-input" value={weight} onChange={(e) => setWeight(e.target.value)} /></div>
        <div className="form-group"><label className="form-label">Répétitions</label>
          <input type="number" min="1" max="30" className="form-input" value={reps} onChange={(e) => setReps(e.target.value)} /></div>
        <div className="form-group"><label className="form-label">Formule</label>
          <select className="form-select" value={formula} onChange={(e) => setFormula(e.target.value)}>
            <option value="average">Moyenne</option>
            {Object.entries(FORMULAS).map(([k, f]) => <option key={k} value={k}>{f.label}</option>)}
          </select></div>
      </div>
      {oneRm ? (
        <>
          <div className="result-big">1RM estimé : <strong>{fmt(Math.round(oneRm * 10) / 10)} kg</strong></div>
          <table className="tool-table">
            <thead><tr><th>%</th><th>Charge</th><th>Reps ≈</th></tr></thead>
            <tbody>
              {table.map((r) => <tr key={r.pct}><td>{r.pct} %</td><td><strong>{fmt(r.weight)} kg</strong></td><td>{r.reps}</td></tr>)}
            </tbody>
          </table>
        </>
      ) : <p className="muted">Saisis un poids et un nombre de répétitions.</p>}
    </div>
  );
}

function WarmupTool() {
  const [work, setWork] = useState('');
  const [bar, setBar] = useState('20');
  const sets = warmupSets(num(work), { bar: num(bar) || 0 });

  return (
    <div className="card">
      <div className="card-title">🔺 Échauffement pyramidal</div>
      <div className="form-row">
        <div className="form-group"><label className="form-label">Charge de travail (kg)</label>
          <input type="number" min="0" step="0.5" className="form-input" value={work} onChange={(e) => setWork(e.target.value)} /></div>
        <div className="form-group"><label className="form-label">Poids de la barre (kg)</label>
          <input type="number" min="0" step="0.5" className="form-input" value={bar} onChange={(e) => setBar(e.target.value)} /></div>
      </div>
      {sets.length ? (
        <ol className="warmup-list">
          {sets.map((s, i) => (
            <li key={i}><span className="set-num">{i + 1}</span><strong>{fmt(s.weight)} kg</strong> × {s.reps}<span className="muted small">{s.label}</span></li>
          ))}
          <li className="warmup-work"><span className="set-num">🎯</span><strong>{fmt(num(work))} kg</strong> — séries de travail</li>
        </ol>
      ) : <p className="muted">Saisis ta charge de travail pour obtenir la progression.</p>}
    </div>
  );
}

function PlatesTool() {
  return (
    <div className="card">
      <div className="card-title">🛞 Calculateur de disques</div>
      <PlateCalculator />
    </div>
  );
}

function PersonalBests() {
  const { data: bests = [] } = useQuery({ queryKey: ['bests'], queryFn: api.getBests });
  if (!bests.length) return null;
  return (
    <div className="card">
      <div className="card-title">🏆 Mes records</div>
      <p className="muted small">Bases utiles pour le calcul des pourcentages.</p>
      <ul className="bests-list">
        {bests.filter((b) => b.max_weight > 0).slice(0, 12).map((b) => (
          <li key={b.exercise_id}><span>{b.exercise_name}</span><strong>{b.max_weight} kg</strong></li>
        ))}
      </ul>
    </div>
  );
}

export default function Tools() {
  return (
    <div>
      <h1 className="page-title">Outils</h1>
      <p className="page-subtitle">1RM, pourcentages, échauffement et disques.</p>
      <div className="grid grid-2">
        <OneRmTool />
        <div className="grid">
          <WarmupTool />
          <PlatesTool />
        </div>
      </div>
      <PersonalBests />
    </div>
  );
}
